import React from 'react';
import { X, Check, UserPlus, UserX } from 'lucide-react';
import { format } from 'date-fns';
import Button from './Button';
import UserName from './UserName';
import Avatar from './Avatar';
import type { MembershipRequest } from '../types';

interface MembershipRequestsModalProps {
  requests: MembershipRequest[];
  onClose: () => void;
  onAction: (requestId: string, action: 'approve' | 'reject') => Promise<void>;
}

export default function MembershipRequestsModal({
  requests,
  onClose,
  onAction
}: MembershipRequestsModalProps) {
  const [processingIds, setProcessingIds] = React.useState<Record<string, boolean>>({});
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleAction = async (requestId: string, action: 'approve' | 'reject') => {
    setProcessingIds(prev => ({ ...prev, [requestId]: true }));
    setErrorMessage(null);
    
    try {
      await onAction(requestId, action);
    } catch (error: any) {
      console.error(`Error ${action === 'approve' ? 'approving' : 'rejecting'} request:`, error);
      setErrorMessage(
        error.message || 
        `Failed to ${action === 'approve' ? 'approve' : 'reject'} the request. Please try again.`
      );
    } finally {
      setProcessingIds(prev => ({ ...prev, [requestId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-10"
  onClick={(e) => {
    if (e.target === e.currentTarget) {
      onClose(); // 👈 Close when clicking outside
    }
  }}
    >
      <div className="bg-surface-light rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-neutral border-b border-neutral p-4 flex items-start justify-between shadow-md z-10">
          <div className="flex items-center">
            <h2 className="text-2xl font-bold font-dillan text-secondary">Membership Requests</h2>
            <span className="ml-2 text-sm text-neutral">({requests.length})</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral" />
          </button>
        </div>

        <div className="p-4">
          {errorMessage && (
            <div className="mb-4 bg-secondary-dark bg-opacity-10 text-secondary-dark p-3 rounded-lg">
              {errorMessage}
            </div>
          )}
          
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <UserPlus className="w-12 h-12 text-neutral mx-auto mb-2 opacity-50" />
              <p className="text-neutral">No pending membership requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-surface-light rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Avatar
                        src={request.user.avatar_url}
                        alt={request.user.display_name || request.user.full_name}
                        size="md"
                      />
                      <div>
                        <UserName
                          name={request.user.display_name || request.user.full_name}
                          verified={request.user.verified}
                          size="sm"
                          userId={request.user.id}
                        />
                        <p className="text-xs text-neutral">
                          {format(new Date(request.created_at), 'PPp')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {request.message && (
                    <div className="mb-4 px-3 py-2 bg-surface rounded-lg text-sm text-neutral">
                      {request.message}
                    </div>
                  )}

                  <div className="flex gap-2 justify-end">
                    <Button

                      onClick={() => handleAction(request.id, 'reject')}
                      className="w-24 px-1 py-0.5 h-8 text-xs bg-neutral text-surface-light hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                      disabled={processingIds[request.id]}
                    >
                      <UserX className="w-3 h-3 mr-0" />
                      Reject
                    </Button>
                    <Button
                      className="w-24 px-1 py-0.5 h-8 text-xs bg-neutral text-surface-light hover:bg-primary-dark hover:text-primary border border-neutral hover:border-primary-dark"
                      onClick={() => handleAction(request.id, 'approve')}
                      disabled={processingIds[request.id]}
                    >
                      <Check className="w-3 h-3 mr-0" />
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}